import axios from "axios";
import { httpClient } from "../http/client";

export type FileUploadPrefix = "circulars" | "logos" | (string & {});

export interface UploadedFileOut {
  id: number;
}

/** Response from `GET /api/v1/files/{fileId}/download-url`. */
export interface FileDownloadUrlOut {
  url: string;
  expiresAt?: string;
}

interface ErrorResponseShape {
  message?: unknown;
  detail?: unknown;
  error?: unknown;
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function getFileUploadErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Unable to connect to server.";
    }

    const status = error.response.status;
    if (status === 401) {
      return "Authentication failed.";
    }
    if (status === 403) {
      return "Access denied.";
    }

    const responseData = error.response.data;
    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }
    if (responseData && typeof responseData === "object") {
      const typedData = responseData as ErrorResponseShape;
      if (typeof typedData.message === "string" && typedData.message.trim()) {
        return typedData.message;
      }
      if (typeof typedData.detail === "string" && typedData.detail.trim()) {
        return typedData.detail;
      }
      if (typeof typedData.error === "string" && typedData.error.trim()) {
        return typedData.error;
      }
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

function parseUploadedFileId(raw: unknown): number {
  const root = asRecord(raw) ?? {};
  const nested =
    asRecord(root.data)
    ?? asRecord(root.result)
    ?? asRecord(root.file)
    ?? root;

  const id =
    toNumber(nested.id)
    ?? toNumber(nested.fileId)
    ?? toNumber(nested.file_id)
    ?? toNumber(root.id)
    ?? toNumber(root.fileId)
    ?? toNumber(root.file_id);

  if (id == null) {
    throw new Error("File upload response did not include an id.");
  }

  return id;
}

function parseDownloadUrlResponse(raw: unknown): FileDownloadUrlOut {
  const root = asRecord(raw) ?? {};
  const nested =
    asRecord(root.data)
    ?? asRecord(root.result)
    ?? root;

  const urlCandidate =
    (typeof nested.url === "string" && nested.url.trim() ? nested.url.trim() : null)
    ?? (typeof nested.downloadUrl === "string" && nested.downloadUrl.trim()
      ? nested.downloadUrl.trim()
      : null)
    ?? (typeof nested.signedUrl === "string" && nested.signedUrl.trim()
      ? nested.signedUrl.trim()
      : null)
    ?? (typeof root.url === "string" && root.url.trim() ? root.url.trim() : null);

  if (!urlCandidate) {
    throw new Error("Download-url response did not include a url.");
  }

  const expiresAtRaw =
    nested.expiresAt
    ?? nested.expires_at
    ?? root.expiresAt
    ?? root.expires_at;
  const expiresAt =
    typeof expiresAtRaw === "string" && expiresAtRaw.trim()
      ? expiresAtRaw.trim()
      : undefined;

  return expiresAt ? { url: urlCandidate, expiresAt } : { url: urlCandidate };
}

function openSignedDownloadUrl(signedUrl: string, filename?: string): void {
  const method = "anchor.click(href=signedUrl, target=_blank)";
  console.log("[CircularDownload] download method being used", method, {
    signedUrl,
    filename: filename ?? "(none — rely on Content-Disposition)",
    note: "Direct browser navigation; no Authorization header; no fetch/CORS.",
  });

  const anchor = document.createElement("a");
  anchor.href = signedUrl;
  // Best-effort: browsers often ignore `download` for cross-origin URLs.
  if (filename?.trim()) {
    anchor.download = filename.trim();
  }
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

function resolveSignedDownloadUrl(rawSignedUrl: string): string {
  const trimmed = rawSignedUrl.trim();
  if (!trimmed) {
    throw new Error("Signed download URL was empty.");
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const baseURL = String(httpClient.defaults.baseURL ?? "").replace(/\/$/, "");
  if (!baseURL) {
    throw new Error("Signed download URL is relative and API base URL is not configured.");
  }

  return `${baseURL}${trimmed.startsWith("/") ? "" : "/"}${trimmed}`;
}

function assertSignedUrlUsable(signedUrl: string, expiresAt?: string): void {
  if (!/^https:\/\//i.test(signedUrl)) {
    // Allow http only for local/dev absolute URLs; still reject empty/relative leftovers.
    if (!/^http:\/\//i.test(signedUrl)) {
      throw new Error(`Signed download URL is not absolute: ${signedUrl}`);
    }
    console.warn("[CircularDownload] Signed URL is http (not https) — mixed content risk on https pages", {
      signedUrl,
    });
  }

  if (expiresAt) {
    const expiresMs = Date.parse(expiresAt);
    if (Number.isFinite(expiresMs) && expiresMs <= Date.now()) {
      throw new Error(`Signed download URL is expired (expiresAt=${expiresAt}).`);
    }
  }
}

/** Relative content path for a stored file (signed URLs still point here). */
export function buildFileContentPath(fileId: number | string): string {
  return `/api/v1/files/${encodeURIComponent(String(fileId))}/content`;
}

/** Relative path to request a temporary signed download URL. */
export function buildFileDownloadUrlPath(fileId: number | string): string {
  return `/api/v1/files/${encodeURIComponent(String(fileId))}/download-url`;
}

/**
 * Step 1 of the file attach flow.
 * POST /api/v1/files (multipart/form-data: file + prefix)
 */
export async function uploadFile(
  file: File,
  prefix: FileUploadPrefix,
): Promise<UploadedFileOut> {
  try {
    const body = new FormData();
    body.append("file", file);
    body.append("prefix", prefix);

    const response = await httpClient.post<unknown>("/api/v1/files", body, {
      headers: {
        Accept: "application/json",
        // Let the browser set multipart boundary; do not force Content-Type.
      },
    });

    const fileId = parseUploadedFileId(response.data);
    console.info("[files] POST /api/v1/files succeeded", {
      prefix,
      status: response.status,
      uploadResponse: response.data,
      fileId,
      requestContentType: response.config.headers?.["Content-Type"]
        ?? response.config.headers?.["content-type"]
        ?? "(browser multipart boundary)",
    });

    return { id: fileId };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("[files] POST /api/v1/files failed", {
        prefix,
        status: error.response?.status,
        responseData: error.response?.data,
        requestHeaders: {
          Accept: error.config?.headers?.Accept ?? error.config?.headers?.accept,
          "Content-Type":
            error.config?.headers?.["Content-Type"]
            ?? error.config?.headers?.["content-type"],
        },
      });
    } else {
      console.error("[files] POST /api/v1/files failed (non-HTTP)", { prefix, error });
    }
    throw new Error(getFileUploadErrorMessage(error, "Failed to upload file."));
  }
}

/**
 * Request a temporary signed download URL for a stored file.
 * GET /api/v1/files/{fileId}/download-url (authenticated).
 */
export async function fetchFileDownloadUrl(
  fileId: number | string,
  options?: {
    /** Override Authorization (e.g. Spot Viewer Basic). */
    authorization?: string;
  },
): Promise<FileDownloadUrlOut> {
  const requestPath = buildFileDownloadUrlPath(fileId);
  console.log("[CircularDownload] fileId", fileId);

  try {
    const response = await httpClient.get<unknown>(requestPath, {
      headers: {
        Accept: "application/json",
        ...(options?.authorization ? { Authorization: options.authorization } : {}),
      },
    });

    console.log("[CircularDownload] download-url API response", response.data);
    const parsed = parseDownloadUrlResponse(response.data);
    console.log("[CircularDownload] signed URL (exact from API)", parsed.url, {
      expiresAt: parsed.expiresAt,
      isAbsoluteHttps: /^https:\/\//i.test(parsed.url),
      isAbsoluteHttp: /^http:\/\//i.test(parsed.url),
      length: parsed.url.length,
    });
    return parsed;
  } catch (error) {
    console.error("[CircularDownload] download-url request failed", {
      fileId,
      requestPath,
      error,
      stack: error instanceof Error ? error.stack : undefined,
    });
    throw new Error(getFileUploadErrorMessage(error, "Failed to get download URL."));
  }
}

/**
 * Download a previously uploaded file via the signed-URL flow:
 * 1. GET /api/v1/files/{fileId}/download-url (authenticated via httpClient)
 * 2. Open the returned signed `url` directly in the browser (no auth, no fetch)
 */
export async function downloadFile(
  fileId: number | string,
  options?: {
    filename?: string;
    /** Override Authorization for download-url step (e.g. Spot Viewer Basic). */
    authorization?: string;
  },
): Promise<void> {
  try {
    const { url: rawSignedUrl, expiresAt } = await fetchFileDownloadUrl(fileId, {
      authorization: options?.authorization,
    });

    const signedUrl = resolveSignedDownloadUrl(rawSignedUrl);
    console.log("[CircularDownload] signed URL (resolved for open)", signedUrl, {
      rawSignedUrl,
      expiresAt,
    });
    assertSignedUrlUsable(signedUrl, expiresAt);

    openSignedDownloadUrl(signedUrl, options?.filename);

    console.log("[CircularDownload] download success", {
      fileId,
      filename: options?.filename,
      signedUrl,
      method: "anchor.click(href=signedUrl, target=_blank)",
    });
  } catch (error) {
    console.error("[CircularDownload] download failure", {
      fileId,
      filename: options?.filename,
      error,
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      completeFetchErrorStack: error instanceof Error ? error.stack : undefined,
    });
    if (error instanceof Error && error.message.trim()) {
      throw error;
    }
    throw new Error(getFileUploadErrorMessage(error, "Failed to download file."));
  }
}
